import { Box, Divider, Heading, Image } from '@wix/design-system';
import { ArrowLeftSmall, ArrowRightSmall } from '@wix/wix-ui-icons-common';
import type { FC } from 'react';
import React from 'react';
import { WixCompanyLogoSquare } from './wix-company-logo-square';
import type { AppDataType } from '@wix/app-market-services';
import { isAppWithBenefits } from '@wix/app-market-services';
import css from './app-page-lean-header.module.scss';
import AppBadges from '../../components/badges/app-badges';
import type { PricingBadgeProps } from '../app-info-badge';
import { AppInfoBadge } from '../app-info-badge';
import { useSiteContext, withSiteContext } from '../../contexts/site-context';
import {
  useEssentials,
  withEssentialsContext,
} from '../../contexts/essentials-context';
import type {
  ComponentsWithSiteContextProps,
  ExternalComponentProps,
} from '../types';
import { AppPageLeanHeaderDataHooks } from './data-hooks';
import { AppReviewsInfo } from '../../components/app-box/app-box-elements/app-reviews-Info/app-reviews-Info';

export interface AppPageLeanHeaderProps
  extends ExternalComponentProps,
    ComponentsWithSiteContextProps {
  image: string;
  appData: Pick<
    AppDataType,
    'name' | 'installation' | 'appBadges' | 'pricing' | 'reviews'
  >;
  showDivider?: boolean;
  isNewSlim?: boolean;
}

const AppPageLeanHeaderComponent: FC<AppPageLeanHeaderProps> = ({
  image,
  appData,
  showDivider = false,
  isNewSlim = false,
}) => {
  const { isPremiumSite } = useSiteContext();
  const { i18n, experiments } = useEssentials();
  const reviews = appData.reviews;

  const headerElement = (
    <Heading
      size="medium"
      as="h1"
      dataHook={AppPageLeanHeaderDataHooks.APP_NAME}
    >
      {appData.name}
    </Heading>
  );
  const appBadgesElement = (
    <AppBadges
      isInstalled={Boolean(appData.installation?.isInstalled)}
      appName={appData.name ?? ''}
      appBadges={appData.appBadges}
      wixSaleSize="small"
    />
  );
  const appInfoBadgeElement = (
    <AppInfoBadge
      i18n={i18n}
      experiments={experiments}
      pricing={appData.pricing as PricingBadgeProps}
      isAppBuiltIn={!!appData.installation?.isBuiltIn}
      isAppInstalled={!!appData.installation?.isInstalled}
      isAppWithBenefits={isAppWithBenefits(appData.appBadges)}
      isAppSetupIncomplete={!!appData.installation?.isSetupIncomplete}
      options={{
        size: 'small',
        showPremiumRequired:
          !!appData.installation?.requirements?.premiumSite && !isPremiumSite,
      }}
    />
  );

  return (
    <Box
      direction="vertical"
      className={css.container}
      dataHook={AppPageLeanHeaderDataHooks.CONTAINER}
    >
      <Box
        gap="SP2"
        verticalAlign={isNewSlim ? 'top' : 'middle'}
        paddingBottom={showDivider ? '18px' : '0'}
        className={!isNewSlim ? css.headerWrapper : ''}
      >
        <Box
          verticalAlign={isNewSlim ? 'top' : 'middle'}
          className={css.appLogo}
          dataHook={AppPageLeanHeaderDataHooks.HEADER_LOGOS}
        >
          {!isNewSlim && (
            <Box
              direction="horizontal"
              dataHook={AppPageLeanHeaderDataHooks.WIX_LOGO}
            >
              <WixCompanyLogoSquare />

              <Box verticalAlign="middle" direction="vertical">
                <ArrowLeftSmall />
                <ArrowRightSmall />
              </Box>
            </Box>
          )}

          <Image
            width="41px"
            height="41px"
            src={image}
            dataHook={AppPageLeanHeaderDataHooks.LOGO_IMAGE}
          />
        </Box>

        <Box direction="vertical">
          {isNewSlim ? (
            appBadgesElement
          ) : (
            <Box
              gap="SP1"
              dataHook={AppPageLeanHeaderDataHooks.APP_NAME_AND_BADGES}
            >
              {headerElement}
              {appBadgesElement}
            </Box>
          )}

          {isNewSlim ? headerElement : appInfoBadgeElement}

          {isNewSlim && reviews && (
            <Box
              direction="horizontal"
              gap="SP2"
              dataHook={AppPageLeanHeaderDataHooks.REVIEWS_INFO}
            >
              <AppReviewsInfo reviews={reviews} />
              <Divider direction="vertical" />
              {appInfoBadgeElement}
            </Box>
          )}
        </Box>
      </Box>

      {showDivider && <Divider />}
    </Box>
  );
};

export const AppPageLeanHeader = withSiteContext(
  withEssentialsContext(AppPageLeanHeaderComponent),
);
